import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Delete02Icon, Exchange01Icon, UploadCircle01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import { IDV_OVERALL_STATUS, IDV_OVERALL_STATUS_DISPLAY } from 'constants/idv';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { uploadSigned } from 'helpers/aws-s3';
import { verifyIdDocument } from 'helpers/document-upload-helpers';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';
import { useParams } from 'react-router-dom';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import styles from './MultipleDocumentUpload.module.scss';

// Stable defaults to avoid effect re-running every render (default params create new refs each time)
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const MultipleDocumentUpload = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  question,
  additionalInstructions = '',
  value = EMPTY_ARRAY, // array of ids or models depending on useModel
  modelParams = {},
  className,
  bucketFolder = FilesFolders.CandidatesApplicationDocuments,
  maxSize = 20971520,
  caption = '',
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType = '',
  brand,
  useModel = false,
  disabled = false,
  metadata = EMPTY_OBJECT,
  listCode = '',
  verifyDocument = false,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  handleStateOnVerify = () => {},
}) => {
  const { t } = useTranslation();
  const title = documentTitle || question?.text || question?.title || 'Select your files to upload';
  const description = documentDescription || question?.description || '';
  const { token } = useParams();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);

  const idUploadTranslations = t(`idUpload`, { ns: 'id_document_types', returnObjects: true });

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();
  const maxRetries = 3;

  const [uploads, setUploads] = useState([]);

  const [loadingMap, setLoadingMap] = useState({});
  const [errorsMap, setErrorsMap] = useState({});

  // Keep ref in sync so value-sync effect can avoid overwriting during multi-file upload
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      // Don't overwrite local state while we have temp entries (multi-file upload in progress)
      if (uploadsRef.current?.some(u => u._tempId)) {
        return;
      }

      if (!value || !Array.isArray(value) || value.length === 0) {
        if (mounted)
          setUploads(prev => {
            if (prev.length === 0) return prev;
            // Don't clear if we have committed uploads (with id) and value is empty – parent may not have synced yet (e.g. onChange was deferred)
            const hasCommitted = prev.some(u => u.id && !u._tempId);
            return hasCommitted ? prev : [];
          });
        return;
      }

      if (useModel) {
        // ensure metadata shape and default attempts
        const normalized = value.map(v => ({
          ...v,
          metadata: v?.metadata || { ...metadata, id_type: idType, attempts: 0 },
        }));
        if (mounted) setUploads(normalized);
        return;
      }
      try {
        // Since value is an array of objects, treat it as useModel mode
        const normalized = value.map(v => ({
          ...v,
          metadata: v?.metadata || { ...metadata, id_type: idType, attempts: 0 },
        }));
        if (mounted) setUploads(normalized);
      } catch (e) {
        console.error('Error processing uploads:', e);
        if (mounted) setUploads([]);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [value, useModel, metadata, idType]);

  // helpers to update per-upload loading/error
  const setLoadingFor = (key, bool) => setLoadingMap(prev => ({ ...prev, [key]: bool }));
  const setErrorFor = (key, message) => setErrorsMap(prev => ({ ...prev, [key]: message }));

  const callOnChange = useCallback(
    newUploads => {
      // Since we're working with objects, always pass them directly
      onChange(newUploads);
    },
    [onChange],
  );

  const validateRetryCountForUpload = useCallback(
    upload => {
      const attempts = upload?.metadata?.attempts || 0;
      if (attempts >= maxRetries && upload?.status === IDV_OVERALL_STATUS.FAILED) {
        const message =
          "We've accepted your document. Our support team will manually verify and contact you if needed.";
        setErrorFor(upload.id || upload._tempId, message);
        setUploads(prev => prev.map(u => (u.id === upload.id ? { ...u, status: 'accepted' } : u)));
        callOnChange(
          uploads.map(u => (u.id === upload.id ? { ...upload, status: 'accepted' } : u)),
        );
      }
    },
    [uploads, callOnChange],
  );

  // single-file upload flow (used for both new uploads and when multiple files provided)
  const uploadSingleFile = async ({ file, prevUpload = null, tempId = null }) => {
    const key = prevUpload?.id ?? prevUpload?._tempId ?? tempId ?? file.name;
    setLoadingFor(key, true);
    setErrorFor(key, null);

    try {
      const params = {
        file_name: file.name,
        file_type: file.type,
        file_ext:
          mime.extension(file.type) ||
          (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
          'bin',
        bucket_folder: bucketFolder,
      };

      const signedFile = await signUpload(params);

      await uploadSigned(signedFile.url, file, file.type);

      const existingAttempts = prevUpload?.metadata?.attempts || 0;
      const attempts = prevUpload ? existingAttempts + 1 : 1;
      const uploadMetadata = {
        ...metadata,
        id_type: idType,
        attempts,
        // Store listCode in metadata for non-ID documents (when verifyDocument is false)
        // This allows requirements calculation to work without creating id_upload_detail records
        ...(listCode && !verifyDocument ? { id_requirements_list: listCode } : {}),
      };

      const uploadData = {
        ...modelParams,
        metadata: uploadMetadata,
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      };

      const created = await createUpload(uploadData);

      if (prevUpload?.id && prevUpload.id !== created.id) {
        try {
          await deleteUpload(prevUpload.id);
        } catch (err) {
          console.error('Failed to delete previous upload:', err);
        }
      }

      const isSameEntry = (u, prev) =>
        prev.id != null ? u.id === prev.id : prev._tempId != null && u._tempId === prev._tempId;

      setUploads(prev => {
        if (prevUpload) {
          return prev.map(u => (isSameEntry(u, prevUpload) ? created : u));
        }
        return prev.map(u => (u._tempId === tempId ? created : u));
      });

      const latest = prev => {
        if (prevUpload) {
          return prev.map(u => (isSameEntry(u, prevUpload) ? created : u));
        }
        return prev.map(u => (u._tempId === tempId ? created : u));
      };

      // For documents that need verification, create id_upload_detail record
      // For non-ID documents (verifyDocument=false), we store listCode in metadata instead
      if (verifyDocument && listCode && token && created?.id) {
        try {
          await verifyIdDocument(
            {
              uploadId: created.id,
              metadata: uploadMetadata,
              listCode,
              expiryTolerance: null,
              verifyByToken,
              reTakeSelfie: false,
              getUpload,
              useModel: true,
              value: created,
              skipVerification,
            },
            {
              setVerifying: () => {},
              setStatus: () => {},
              setError: () => {},
              setErrorType: () => {},
              setVerificationError: () => {},
              setShowSelfieModal: () => {},
              onChange: () => {},
              setIsFileRejected: () => {},
            },
          );
        } catch (verifyErr) {
          // Log error but don't block the upload
          console.error('Error creating id_upload_detail:', verifyErr);
        }
      }

      setUploads(prevState => {
        const newList = latest(prevState);
        // Defer so we don't update parent (FormPageRenderer) during this component's render/setState
        queueMicrotask(() => {
          callOnChange(newList);
          validateRetryCountForUpload({ ...created, metadata: uploadMetadata });
        });
        return newList;
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      const message =
        err?.data?.message ??
        err?.message ??
        idUploadTranslations?.reasonForFailure?.default ??
        'Upload failed. Please try again.';
      setErrorFor(key, message);
    } finally {
      setLoadingFor(key, false);
    }
  };

  const handleFileInput = async event => {
    disableFormActions();
    handleStateOnVerify(true);
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      enableFormActions();
      handleStateOnVerify(false);
      return;
    }

    // Add all temp entries in one update so state is stable before uploads run
    const baseTime = Date.now();
    const tempEntries = files.map((file, index) => ({
      _tempId: `temp-${baseTime}-${index}-${file.name}`,
      file_name: file.name,
      metadata: { ...metadata, id_type: idType, attempts: 0 },
      status: null,
    }));
    setUploads(prev => [...prev, ...tempEntries]);

    try {
      for (let i = 0; i < files.length; i++) {
        if (!isFileAllowed(files[i])) {
          setErrorFor(
            tempEntries[i]._tempId,
            `This file type is not supported. Please use ${allowedLabel}.`,
          );
          continue;
        }
        if (files[i].size > maxSize) {
          setErrorFor(
            tempEntries[i]._tempId,
            `File is too large. Max size is ${maxSize / 1024 / 1024}MB. Please upload a smaller file.`,
          );
          continue;
        }
        await uploadSingleFile({
          file: files[i],
          prevUpload: null,
          tempId: tempEntries[i]._tempId,
        });
      }
    } finally {
      enableFormActions();
      handleStateOnVerify(false);
      event.target.value = '';
    }
  };

  const handleSelectReplaceFile = upload => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = effectiveAccept;
    input.onchange = e => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      handleReplace(upload, file);
    };
    input.click();
  };

  const handleReplace = async (existingUpload, newFile) => {
    if (!isFileAllowed(newFile)) {
      setErrorFor(
        existingUpload.id || existingUpload._tempId,
        `This file type is not supported. Please use ${allowedLabel}.`,
      );
      return;
    }
    await uploadSingleFile({ file: newFile, prevUpload: existingUpload });
  };

  const handleFileDelete = async upload => {
    const key = upload.id || upload._tempId || upload.file_name;
    setLoadingFor(key, true);
    setErrorFor(key, null);
    try {
      if (upload.id) {
        await deleteUpload(upload.id);
      }
      const updated = uploads.filter(u =>
        u.id ? u.id !== upload.id : u._tempId !== upload._tempId,
      );
      setUploads(updated);
      callOnChange(updated);
    } catch (err) {
      console.error('Error deleting file:', err);
      setErrorFor(key, 'Delete failed. Please try again.');
    } finally {
      setLoadingFor(key, false);
    }
  };

  const renderStatusPill = upload => {
    const status = upload?.status || upload?.verification_status || null; // support variations
    const attempts = upload?.metadata?.attempts || 0;
    if (status === IDV_OVERALL_STATUS.FAILED) {
      if (attempts < maxRetries) {
        return (
          <StatusPill
            text={IDV_OVERALL_STATUS_DISPLAY[status]?.title || 'Failed'}
            status={IDV_OVERALL_STATUS_DISPLAY[status]?.color || 'error'}
          />
        );
      }
      if (status === 'accepted') {
        return <StatusPill text="Support team to verify" status="warning" />;
      }
    }

    if (status && status === IDV_OVERALL_STATUS.VERIFIED) {
      return (
        <StatusPill
          text={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].title}
          status={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].color}
        />
      );
    }

    return (
      <StatusPill
        text={upload.file_name ? 'Uploaded' : 'Not Yet Uploaded'}
        status={upload.file_name ? 'success' : 'neutral'}
      />
    );
  };

  return (
    <>
      <div
        className={cn(styles.root, { [className]: className, 'u-disabled': disabled })}
        style={brandingStyleVariables}>
        <div
          className={cn(
            'u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium',
          )}>
          <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
            <div className="d-flex flex-column u-gap u-max-upload-card-width">
              {caption && (
                <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
              )}
              <div className="d-flex flex-column u-gap-x-small">
                <span className="u-font-size-large u-font-weight-500">{title}</span>
                {description && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                    {description}
                  </span>
                )}
                <span className="text-lg text-neutral-6">Max size: {maxSize / 1024 / 1024}MB</span>
                {additionalInstructions && (
                  <span
                    className={cn(
                      'secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line',
                      description && 'u-margin-top--small',
                    )}>
                    {additionalInstructions}
                  </span>
                )}
              </div>
            </div>

            <label
              className={cn(styles.uploadButton, 'u-border', { 'u-disabled': disabled })}
              style={{
                '--brand-button-color': brandingStyleVariables['--button-color'],
                '--brand-button-text-color': brandingStyleVariables['--button-text-color'],
              }}>
              <input
                type="file"
                accept={effectiveAccept}
                multiple
                onChange={handleFileInput}
                disabled={disabled}
                style={{ display: 'none' }}
              />
              <HugeiconsIcon size="20" icon={UploadCircle01Icon} strokeWidth="1.5" />
              <span className="u-font-size-small u-font-weight-400 primary-text">Upload Files</span>
            </label>
          </div>

          {uploads?.length > 0 && (
            <div className="d-flex flex-column u-gap">
              {uploads.map((item, index) => {
                const key = item.id || item._tempId || item.file_name;
                const error = errorsMap[key];
                const listKey = `${key ?? 'item'}-${index}`;
                return (
                  <div
                    key={listKey}
                    className="d-flex justify-content-between align-items-center u-border-dashed u-border-radius u-padding surface-tertiary-light">
                    <div className={cn(styles.contentColumn, 'd-flex flex-column u-gap')}>
                      <span
                        className={cn(styles.wrappingText, 'u-font-size-small u-font-weight-500')}>
                        {item.file_name}
                      </span>
                      <div className="d-flex u-gap align-items-center">
                        {item.metadata?.attempts != null && <>{renderStatusPill(item)}</>}
                        {item.status && (
                          <span className="u-font-size-x-small secondary-text">
                            Status: {item.status}
                          </span>
                        )}
                      </div>
                      {error && (
                        <div className="u-margin-top--xxsmall">
                          <FormMessage
                            message={error}
                            type={MessageType.Error}
                            noMargin
                            closeable={false}
                          />
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-items-center u-gap">
                      <Button
                        type="secondary"
                        edged
                        onClick={() => handleSelectReplaceFile(item)}
                        disabled={disabled || !!loadingMap[key]}>
                        <HugeiconsIcon
                          size="20"
                          icon={Exchange01Icon}
                          strokeWidth="1.5"
                          className="u-margin-right--small"
                        />
                        Replace
                      </Button>

                      <IconComponent
                        icon={Delete02Icon}
                        onClick={() => handleFileDelete(item)}
                        tip="Delete"
                        type="error"
                        size="small"
                        noBackground
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {needsComplementaryDoc && complementaryInstructions && (
            <DisplayBox show={true} alignLeft>
              <span className="u-font-size-small u-font-weight-400">
                {complementaryInstructions}
              </span>
            </DisplayBox>
          )}
        </div>
      </div>
    </>
  );
};

MultipleDocumentUpload.propTypes = {
  documentTitle: PropTypes.string,
  documentDescription: PropTypes.string,
  question: PropTypes.object,
  value: PropTypes.array,
  modelParams: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  metadata: PropTypes.object,
  listCode: PropTypes.string,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  needsComplementaryDoc: PropTypes.bool,
  complementaryInstructions: PropTypes.string,
};
